import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2, Gift } from 'lucide-react';

export default function Signup() {
  const [params] = useSearchParams();
  const refFromUrl = (params.get('ref') || '').toUpperCase();
  const planTokenFromUrl = params.get('planToken') || '';
  const planFromUrl = params.get('plan') || '';
  const [form, setForm] = useState({
    name: '', email: '', phone: '', password: '', confirm: '',
    // If a plan was pre-paid, they're a Business Owner by definition.
    group: planTokenFromUrl ? 'Business Owner' : '',
    ref: refFromUrl,
    smsOptIn: true,
    purchaseToken: planTokenFromUrl,
    consumerRoleEnabled: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { if (refFromUrl) setForm(p => ({ ...p, ref: refFromUrl })); }, [refFromUrl]);
  // If arrived without ?planToken= but a token is stashed in sessionStorage
  // (e.g. deep-linked after checkout), pick it up so the account inherits it.
  useEffect(() => {
    if (planTokenFromUrl) return;
    try {
      const t = sessionStorage.getItem('pbb_plan_purchase_token');
      if (t) setForm(p => ({ ...p, purchaseToken: t, group: p.group || 'Business Owner' }));
    } catch {}
  }, [planTokenFromUrl]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name || !form.email || !form.phone || !form.password || !form.group) {
      setError('Please complete all required fields'); return;
    }
    if (form.password !== form.confirm) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      // Only forward the fields the backend expects (drop 'confirm').
      const { confirm, ...payload } = form;
      // If they pre-paid a plan, the payment is already captured — send them
      // to Manage Listings so they can attach it to their first business.
      const hasPrepaidPlan = !!payload.purchaseToken;
      const paidGroups = ['Business Owner', 'Group', 'Nonprofit'];
      const isPaid = paidGroups.includes(payload.group);
      const target = hasPrepaidPlan ? '/business/manage?create=1' : '/';
      if (isPaid || hasPrepaidPlan) {
        try { sessionStorage.setItem('post_signup_redirect', target); } catch (_e) {}
      }
      await signup(payload);
      try {
        sessionStorage.removeItem('pbb_plan_purchase_token');
        sessionStorage.removeItem('pbb_plan_purchase_pkg');
      } catch {}
      if (isPaid || hasPrepaidPlan) {
        window.location.href = target;
        return;
      }
      navigate('/');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const setF = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-center mb-6">
          <div className="w-14 h-14 rounded-full bg-green-600 flex items-center justify-center font-black text-black">PBB</div>
        </div>
        <h1 className="pbb-title-green text-3xl mb-2">Sign up Now</h1>
        <p className="text-sm text-gray-300 mb-6">
          Already have an account?{' '}
          <Link to="/login" className="pbb-link">Sign in</Link>
        </p>
        {form.purchaseToken && (
          <div className="mb-4 rounded-xl border border-green-500/40 bg-green-500/10 p-3 flex items-start gap-2"
               data-testid="signup-prepaid-banner">
            <Gift className="text-green-400 shrink-0 mt-0.5" size={18} />
            <div className="text-sm">
              <div className="font-semibold text-green-300">Your plan is paid for!</div>
              <div className="text-xs text-gray-300 mt-0.5">
                {planFromUrl ? `Your ${planFromUrl.charAt(0).toUpperCase() + planFromUrl.slice(1)} plan` : 'Your plan'} will
                activate on the first business you register after creating your account.
              </div>
            </div>
          </div>
        )}
        <form onSubmit={submit} className="space-y-4">
          <input className="pbb-input" placeholder="Full Name *" value={form.name} onChange={e => setF('name', e.target.value)}
            autoComplete="name" autoCapitalize="words" autoCorrect="off" spellCheck={false} />
          <input className="pbb-input" type="email" placeholder="Email *" value={form.email} onChange={e => setF('email', e.target.value)}
            autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
          <div className="flex gap-2">
            <div className="pbb-input flex items-center justify-center" style={{width:'80px', padding:'14px 8px'}}>+1</div>
            <input className="pbb-input" placeholder="Phone *" value={form.phone} onChange={e => setF('phone', e.target.value)}
              type="tel" autoComplete="tel" autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="tel" />
          </div>
          <input className="pbb-input" type="password" placeholder="Password *" value={form.password} onChange={e => setF('password', e.target.value)}
            autoComplete="new-password" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
          <input className="pbb-input" type="password" placeholder="Confirm Password *" value={form.confirm} onChange={e => setF('confirm', e.target.value)}
            autoComplete="new-password" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
          <select className="pbb-input" value={form.group} onChange={e => setF('group', e.target.value)}>
            <option value="">Group *</option>
            <option value="Consumer">Consumer</option>
            <option value="Business Owner">Business Owner</option>
            <option value="Young Leader">Young Leader</option>
            <option value="Group">Group</option>
            <option value="Nonprofit">Nonprofit</option>
            <option value="Artist">Artist</option>
            <option value="Author">Author</option>
          </select>

          {/* Dual-role opt-in — only relevant for Business Owners */}
          {form.group === 'Business Owner' && (
            <label htmlFor="signup-consumer-role"
              className="flex items-start gap-3 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 cursor-pointer select-none"
              data-testid="signup-consumer-role-label">
              <input
                id="signup-consumer-role"
                type="checkbox"
                data-testid="signup-consumer-role"
                checked={!!form.consumerRoleEnabled}
                onChange={e => setF('consumerRoleEnabled', e.target.checked)}
                className="mt-1 h-4 w-4 shrink-0 accent-emerald-500 cursor-pointer"
              />
              <span className="text-[11px] leading-relaxed text-emerald-200">
                <b>Also activate my Consumer role</b> — earn points and redeem deals from OTHER businesses. I can't earn or redeem on my own listings.
              </span>
            </label>
          )}

          {/* SMS opt-in — TCPA-compliant explicit consent. Terms & Privacy open
              in new tabs so the signup form state is preserved. */}
          <label
            htmlFor="sms-opt-in"
            className="flex items-start gap-3 rounded-lg border border-[#153b23] bg-[#0d1f14]/60 p-3 cursor-pointer select-none"
          >
            <input
              id="sms-opt-in"
              type="checkbox"
              data-testid="signup-sms-opt-in"
              checked={form.smsOptIn}
              onChange={e => setF('smsOptIn', e.target.checked)}
              className="mt-1 h-4 w-4 shrink-0 accent-green-500 cursor-pointer"
            />
            <span className="text-[11px] leading-relaxed text-gray-300">
              I agree to receive SMS notifications from Promote Black Business.
              View our{' '}
              <Link
                to="/terms"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                data-testid="signup-terms-link"
                className="text-green-400 hover:text-green-300 underline underline-offset-2 font-medium"
              >
                Terms and Conditions
              </Link>{' '}
              and{' '}
              <Link
                to="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                data-testid="signup-privacy-link"
                className="text-green-400 hover:text-green-300 underline underline-offset-2 font-medium"
              >
                Privacy Policy
              </Link>
              . Message and data rates may apply.
            </span>
          </label>
          {refFromUrl && (
            <div className="rounded-lg bg-green-600/10 border border-green-600/40 px-3 py-2 text-xs text-green-300 inline-flex items-center gap-2" data-testid="ref-banner">
              <Gift size={14}/> Referral code <b className="font-mono">{refFromUrl}</b> applied. You and your friend both earn +100 pts per loyalty milestone.
            </div>
          )}
          {error && <div className="text-red-400 text-sm">{error}</div>}
          <button type="submit" disabled={loading} className="pbb-btn-green disabled:opacity-70 inline-flex items-center justify-center gap-2">
            {loading && <Loader2 className="animate-spin" size={16}/>}
            {loading ? 'Creating account...' : 'Sign up'}
          </button>
          <p className="text-center text-xs text-gray-400 mt-3">
            By signing up, you agree to our{' '}
            <Link to="/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-green-300">Terms of Use</Link>
            {' '}and{' '}
            <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-green-300">Privacy Policy</Link>.
            Get <b className="text-green-400">1,000 pts</b> free at signup.
          </p>
        </form>
      </div>
    </div>
  );
}
